import { useState, useEffect } from 'react';
import { useI18n } from '@/context/I18nContext';
import { Dialog } from '@/components/common/Dialog';
import { rpc, shareSessions } from '@/api/client';
import type { ListSessionsResponse, SessionSummary } from '@/types/telemetry';
import { Search, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';
import { Pagination } from '@/components/common/Pagination';

interface TeamShareWizardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  teamId: string;
}

const PAGE_SIZE = 10;

export function TeamShareWizardDialog({ open, onClose, onSuccess, teamId }: TeamShareWizardDialogProps) {
  const { t } = useI18n();

  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 — share mode + session selection
  const [shareAll, setShareAll] = useState(true);
  const [sessionQuery, setSessionQuery] = useState('');
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionPage, setSessionPage] = useState(1);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Step 2 — confirm
  const [isSharing, setIsSharing] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setShareAll(true);
      setSessionQuery('');
      setSessions([]);
      setTotalSessions(0);
      setSessionPage(1);
      setSelectedSessionIds(new Set());
      setShowOnlySelected(false);
    }
  }, [open]);

  // Load sessions (paginated)
  useEffect(() => {
    if (step !== 1 || shareAll) return;

    setLoadingSessions(true);
    const timeout = setTimeout(() => {
      rpc<ListSessionsResponse>('ListSessions', {
        page: sessionPage,
        pageSize: PAGE_SIZE,
        query: sessionQuery || undefined,
      })
        .then(res => {
          setSessions(res.sessions || []);
          setTotalSessions(res.totalCount ?? 0);
        })
        .catch(err => console.error('Failed to fetch sessions:', err))
        .finally(() => setLoadingSessions(false));
    }, sessionQuery ? 300 : 0);

    return () => clearTimeout(timeout);
  }, [step, shareAll, sessionPage, sessionQuery]);

  const totalPages = Math.max(1, Math.ceil(totalSessions / PAGE_SIZE));

  const toggleSession = (id: string) => {
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleShare = async () => {
    if (!shareAll && selectedSessionIds.size === 0) return;
    setIsSharing(true);
    try {
      const sessionIds = shareAll ? [] : Array.from(selectedSessionIds);
      await shareSessions(crypto.randomUUID(), teamId, shareAll, sessionIds);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to share:', err);
    } finally {
      setIsSharing(false);
    }
  };

  const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString(); } catch { return date; }
  };

  const canGoStep2 = shareAll || selectedSessionIds.size > 0;

  const subtitles: Record<number, string> = {
    1: t('sharing.stepScope'),
    2: t('sharing.stepConfirm'),
  };

  // ── Footer ──
  const footer = (() => {
    if (step === 1) return (
      <div className="flex justify-end w-full">
        <button onClick={() => setStep(2)} disabled={!canGoStep2}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
          {t('sharing.next')} <ArrowRight size={14} />
        </button>
      </div>
    );
    return (
      <div className="flex items-center justify-between w-full">
        <button onClick={() => setStep(1)}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer">
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
        <button onClick={handleShare} disabled={!canGoStep2 || isSharing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
          <Share2 size={14} /> {isSharing ? t('common.loading') : t('sharing.share')}
        </button>
      </div>
    );
  })();

  const visibleSessions = showOnlySelected
    ? sessions.filter(s => selectedSessionIds.has(s.id))
    : sessions;

  return (
    <Dialog open={open} onClose={onClose} title={t('sharing.newShare')} subtitle={subtitles[step]} footer={footer}>

      {/* ════ Step 1: Share mode + session picker ════ */}
      {step === 1 && (
        <div className="flex flex-col gap-5 h-full">
          {/* Share mode dropdown */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">{t('sharing.selectSessions')}</label>
            <select value={shareAll ? 'all' : 'selected'} onChange={e => setShareAll(e.target.value === 'all')}
              className="input-field w-full py-2 px-3 cursor-pointer">
              <option value="all">{t('sharing.shareAll')}</option>
              <option value="selected">{t('sharing.shareSelected')}</option>
            </select>
          </div>

          {/* Session picker */}
          {!shareAll && (
            <div className="flex flex-col gap-3 flex-1 min-h-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-text-secondary">
                  {t('sharing.selectedSessions').replace('{count}', String(selectedSessionIds.size))}
                </span>
                {selectedSessionIds.size > 0 && (
                  <button onClick={() => setShowOnlySelected(!showOnlySelected)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors cursor-pointer ${showOnlySelected ? 'bg-accent-subtle text-accent border-accent/30' : 'text-text-muted border-border-default hover:text-text-secondary'}`}>
                    {t('sharing.showSelected')}
                  </button>
                )}
              </div>

              {/* Session search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={14} />
                <input type="text" value={sessionQuery} onChange={e => { setSessionQuery(e.target.value); setSessionPage(1); }}
                  placeholder={t('sharing.filterSessions')} className="input-field w-full py-1.5 pl-9 pr-3 text-sm" />
              </div>

              {/* Session list */}
              <div className="border border-border-default rounded-md flex-1 min-h-0 overflow-y-auto">
                {loadingSessions ? (
                  <div className="p-6 text-sm text-text-muted text-center">{t('common.loading')}</div>
                ) : visibleSessions.length > 0 ? (
                  visibleSessions.map((s, i) => {
                    const sel = selectedSessionIds.has(s.id);
                    return (
                      <label key={s.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${i < visibleSessions.length - 1 ? 'border-b border-border-subtle' : ''} ${sel ? 'bg-accent-subtle' : 'hover:bg-bg-hover'}`}>
                        <input type="checkbox" checked={sel} onChange={() => toggleSession(s.id)} className="accent-accent shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-text-primary truncate">{s.trackName} — {s.driverName}</div>
                          <div className="text-xs text-text-secondary">{formatDate(s.date)} · {s.completedLaps} {t('sharing.laps')}</div>
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <div className="p-6 text-sm text-text-muted text-center">{t('sharing.noSessions')}</div>
                )}
              </div>

              {/* Pagination */}
              {!showOnlySelected && totalPages > 1 && (
                <Pagination
                  page={sessionPage}
                  totalPages={totalPages}
                  onPageChange={setSessionPage}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ════ Step 2: Confirm ════ */}
      {step === 2 && (
        <div className="space-y-5">
          {/* Summary of what's being shared */}
          <div className="text-sm text-text-secondary">
            {shareAll
              ? t('sharing.sharedAll')
              : t('sharing.selectedSessions').replace('{count}', String(selectedSessionIds.size))}
          </div>
          <div className="text-sm text-text-secondary">
            {t('sharing.shareWithTeam')}
          </div>
        </div>
      )}
    </Dialog>
  );
}
